import { useNavigate } from 'react-router-dom'
import { Target, MapPin, TrendingUp, Heart, DollarSign, Clock, Sparkles, Lock, Lightbulb } from 'lucide-react'

const COVER_CARDS = [
  { icon: Target,     title: 'Role Goals',    desc: 'What type of role energises you right now?' },
  { icon: MapPin,     title: 'Location',      desc: 'Where do you want to work?' },
  { icon: TrendingUp, title: 'Progression',   desc: 'What career growth matters most?' },
  { icon: Heart,      title: 'Culture Fit',   desc: 'What environment brings out your best?' },
  { icon: DollarSign, title: 'Compensation',  desc: 'What are your expectations?' },
  { icon: Clock,      title: 'Constraints',   desc: 'Any timing or availability considerations?' },
]

const HOW_IT_WORKS = [
  { step: 1, text: 'Erica introduces herself and explains what she\'ll ask.' },
  { step: 2, text: 'You answer naturally — no right or wrong responses.' },
  { step: 3, text: 'Erica probes deeper on topics that matter most to you.' },
  { step: 4, text: 'A private profile is generated for recruiter matching.' },
]

const QUICK_FACTS = [
  { value: '10–15 min', label: 'Average time' },
  { value: '3×',        label: 'Better match quality' },
  { value: 'Private',   label: 'Confidential responses' },
]

const PRIVACY_BULLETS = [
  'Encrypted and secure',
  'Shared only with approved companies',
  'Edit or delete anytime',
]

const TIPS = [
  'Be honest — Erica is non-judgmental',
  'Think beyond job title',
  'Share your real motivations',
  'Take your time on each answer',
]

export const CandidateDeepDiveStart = () => {
  const navigate = useNavigate()

  return (
    <div className="bg-[#02182B] min-h-screen">
      {/* Page header */}
      <div className="border-b border-[#233D4C]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#FD802E]/10 rounded-lg flex items-center justify-center">
              <Sparkles size={18} className="text-[#FD802E]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">AI Career Deep-Dive</h1>
              <p className="text-gray-400 text-sm mt-0.5">Help us understand what you're looking for</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col lg:flex-row gap-8">

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-8">

          {/* Why this matters */}
          <div>
            <h2 className="text-white font-semibold text-lg mb-4">Why This Matters</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {QUICK_FACTS.map((fact) => (
                <div key={fact.label} className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 text-center">
                  <p className="text-[#FD802E] font-bold text-2xl">{fact.value}</p>
                  <p className="text-gray-400 text-sm mt-1">{fact.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* What we'll cover */}
          <div>
            <h2 className="text-white font-semibold text-lg mb-4">What We'll Cover</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {COVER_CARDS.map((card) => (
                <div key={card.title} className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4">
                  <div className="w-9 h-9 bg-[#FD802E]/10 rounded-lg flex items-center justify-center mb-3">
                    <card.icon size={17} className="text-[#FD802E]" />
                  </div>
                  <p className="text-white font-semibold text-sm">{card.title}</p>
                  <p className="text-gray-400 text-xs mt-1 leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* How it works */}
          <div>
            <h2 className="text-white font-semibold text-lg mb-4">How It Works</h2>
            <div className="space-y-3">
              {HOW_IT_WORKS.map((item) => (
                <div key={item.step} className="flex items-start gap-3">
                  <span className="w-7 h-7 bg-[#FD802E]/10 text-[#FD802E] text-sm font-bold rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    {item.step}
                  </span>
                  <p className="text-gray-300 text-sm leading-relaxed pt-1">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTAs */}
          <div className="space-y-3 pt-2">
            <button
              onClick={() => navigate('/candidate/deep-dive/conversation')}
              className="w-full flex items-center justify-center gap-2 bg-[#FD802E] text-[#233D4C] font-bold py-4 px-6 rounded-lg hover:bg-[#ff8f45] transition-colors text-base"
            >
              <Sparkles size={18} /> Start AI Career Deep-Dive
            </button>
            <button className="w-full text-gray-400 hover:text-white text-sm py-2 transition-colors">
              Skip for Now
            </button>
          </div>

        </div>

        {/* Right sidebar */}
        <div className="w-full lg:w-72 lg:flex-shrink-0 space-y-4">

          {/* Quick facts */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-3">
            <p className="text-xs uppercase text-gray-400 font-medium">Quick Facts</p>
            {QUICK_FACTS.map((fact) => (
              <div key={fact.label} className="flex items-center justify-between text-sm">
                <span className="text-gray-400">{fact.label}</span>
                <span className="text-[#FD802E] font-bold">{fact.value}</span>
              </div>
            ))}
          </div>

          {/* Privacy */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Lock size={13} className="text-[#FD802E]" />
              <p className="text-xs uppercase text-gray-400 font-medium">Your Privacy</p>
            </div>
            <ul className="space-y-2">
              {PRIVACY_BULLETS.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-300">
                  <span className="text-green-400 mt-0.5 shrink-0">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Tips */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Lightbulb size={13} className="text-yellow-400" />
              <p className="text-xs uppercase text-gray-400 font-medium">Tips for Success</p>
            </div>
            <ul className="space-y-2">
              {TIPS.map((tip) => (
                <li key={tip} className="flex items-start gap-2 text-sm text-gray-300">
                  <span className="text-[#FD802E] mt-0.5 shrink-0">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>
    </div>
  )
}
